import type { PatientFamilyHistoryIntakeRecord } from '../types/familyHistoryIntake';

/**
 * Plain-English account of a locked intake: what state it is in, and what
 * happens next.
 *
 * Extracted from FamilyHistoryIntakePage so it can be tested. The copy it
 * replaced built a sentence around the raw status value — "your intake is in
 * submitted" — which is the specific failure the tests here guard against.
 */
export function statusNarrative(status: PatientFamilyHistoryIntakeRecord['status']) {
  switch (status) {
    case 'submitted':
      return {
        title: 'Your history is with your care team.',
        body: 'Thank you — that is everything we needed. A clinician will read it before your review, and we will write to you if anything needs following up. There is nothing else for you to do.',
      };
    case 'care_clinician_review':
    case 'doctor_in_review':
      return {
        title: 'A clinician is reading your history.',
        body: 'It is being reviewed now. We will be in touch if anything needs clarifying — you do not need to do anything in the meantime.',
      };
    // Internally this is the gap where a Relationship Manager picks which
    // senior clinician signs the history off. To the member that is the same
    // fact as sign-off itself — their history is done and with the practice —
    // so it says so rather than exposing an assignment step they have no part
    // in and cannot influence.
    case 'awaiting_senior_assignment':
    case 'medical_director_signoff':
    case 'ops_signoff_pending':
      return {
        title: 'Your history is awaiting final sign-off.',
        body: 'A clinician has been through it and it is with a senior clinician for sign-off. You will be able to request a correction once it is released.',
      };
    case 'released':
    case 'signed_off':
      return {
        title: 'Your history is complete.',
        body: 'It has been reviewed and released. You can read through it any time, and request a correction if something is not right.',
      };
    default:
      return {
        title: 'Your history is locked for now.',
        body: 'You can read through it, and request a correction once it is released.',
      };
  }
}
